// =====================================================
// CIM TEMPLATES BY INDUSTRY
// Customizable templates to improve AI generation quality
// =====================================================

export interface CIMTemplate {
  industry: string;
  industryFr: string;
  executiveSummaryPrompt: string;
  businessOverviewPrompt: string;
  financialHighlightsPrompt: string;
  growthOpportunitiesPrompt: string;
  investmentConsiderationsPrompt: string;
  transitionPlanPrompt: string;
  keyMetrics: string[];
  valuationFactors: string[];
}

export const CIM_TEMPLATES: Record<string, CIMTemplate> = {
  manufacturing: {
    industry: "Manufacturing",
    industryFr: "Manufacturier",
    executiveSummaryPrompt: "Focus on production capacity, equipment value, supply chain relationships, and operational efficiency metrics. Highlight any proprietary processes or certifications.",
    businessOverviewPrompt: "Detail manufacturing capabilities, equipment list, facility specifications, quality control processes, and key customer relationships. Include capacity utilization rates.",
    financialHighlightsPrompt: "Emphasize gross margins, inventory turnover, COGS breakdown, equipment depreciation schedules, and working capital requirements.",
    growthOpportunitiesPrompt: "Identify capacity expansion potential, new product lines, automation opportunities, vertical integration possibilities, and geographic expansion.",
    investmentConsiderationsPrompt: "Address equipment age and replacement costs, supplier concentration, labor relations, environmental compliance, and industry cyclicality.",
    transitionPlanPrompt: "Focus on key employee retention, customer relationship continuity, supplier agreements, and operational knowledge transfer.",
    keyMetrics: ["Capacity Utilization", "Inventory Turnover", "Equipment Age", "Customer Concentration", "Gross Margin"],
    valuationFactors: ["Equipment Value", "Real Estate", "Customer Contracts", "Proprietary Processes"],
  },

  technology: {
    industry: "Technology",
    industryFr: "Technologie",
    executiveSummaryPrompt: "Highlight recurring revenue, technology stack, intellectual property, and market position. Emphasize scalability and customer retention metrics.",
    businessOverviewPrompt: "Detail software/product architecture, development team capabilities, technology roadmap, customer base composition, and competitive advantages.",
    financialHighlightsPrompt: "Focus on MRR/ARR, customer acquisition cost, lifetime value, churn rates, and R&D investment. Include SaaS metrics if applicable.",
    growthOpportunitiesPrompt: "Identify product expansion, market penetration, strategic partnerships, international expansion, and emerging technology adoption.",
    investmentConsiderationsPrompt: "Address technology obsolescence risk, key personnel dependency, customer concentration, competitive landscape, and IP protection.",
    transitionPlanPrompt: "Focus on technical documentation, code ownership transfer, key developer retention, and customer relationship handoff.",
    keyMetrics: ["MRR/ARR", "Churn Rate", "CAC", "LTV", "NRR"],
    valuationFactors: ["Recurring Revenue", "IP Portfolio", "Customer Base", "Technology Stack"],
  },

  healthcare: {
    industry: "Healthcare",
    industryFr: "Santé",
    executiveSummaryPrompt: "Emphasize patient base, insurance/payer relationships, regulatory compliance, and quality of care metrics. Highlight any specialized services.",
    businessOverviewPrompt: "Detail service offerings, patient demographics, facility specifications, staff credentials, referral network, and technology systems.",
    financialHighlightsPrompt: "Focus on revenue per patient, payer mix, reimbursement rates, staffing costs, and compliance-related expenses.",
    growthOpportunitiesPrompt: "Identify service expansion, telehealth adoption, population health trends, partnership opportunities, and geographic expansion.",
    investmentConsiderationsPrompt: "Address regulatory compliance, reimbursement risk, staff retention, malpractice considerations, and facility requirements.",
    transitionPlanPrompt: "Focus on credentialing transfer, patient notification requirements, staff retention, and continuity of care protocols.",
    keyMetrics: ["Patient Volume", "Payer Mix", "Staff Utilization", "Patient Satisfaction", "Reimbursement Rates"],
    valuationFactors: ["Patient Relationships", "Licenses", "Contracts", "Equipment"],
  },

  retail: {
    industry: "Retail",
    industryFr: "Commerce de détail",
    executiveSummaryPrompt: "Focus on brand strength, customer loyalty, omnichannel presence, and inventory management. Highlight same-store sales trends.",
    businessOverviewPrompt: "Detail store locations, e-commerce capabilities, supply chain, vendor relationships, and customer demographics.",
    financialHighlightsPrompt: "Emphasize same-store sales, inventory turnover, gross margins, rent as percentage of revenue, and e-commerce growth.",
    growthOpportunitiesPrompt: "Identify new store locations, e-commerce expansion, private label development, loyalty program enhancement, and market expansion.",
    investmentConsiderationsPrompt: "Address lease obligations, inventory risk, competitive pressure, consumer trends, and seasonality.",
    transitionPlanPrompt: "Focus on vendor relationship continuity, employee retention, customer communication, and operational systems transfer.",
    keyMetrics: ["Same-Store Sales", "Inventory Turnover", "Customer Retention", "Average Transaction", "E-commerce %"],
    valuationFactors: ["Brand Value", "Location Leases", "Customer Database", "Vendor Relationships"],
  },

  professional_services: {
    industry: "Professional Services",
    industryFr: "Services professionnels",
    executiveSummaryPrompt: "Highlight client relationships, staff expertise, billing rates, and client retention. Emphasize recurring engagements and contract values.",
    businessOverviewPrompt: "Detail service offerings, client portfolio, staff credentials, delivery methodology, and competitive positioning.",
    financialHighlightsPrompt: "Focus on utilization rates, average billing rates, client concentration, staff costs as percentage of revenue, and project profitability.",
    growthOpportunitiesPrompt: "Identify service expansion, geographic reach, strategic hires, technology adoption, and vertical specialization.",
    investmentConsiderationsPrompt: "Address key personnel risk, client concentration, competitive landscape, and staff retention challenges.",
    transitionPlanPrompt: "Focus on client relationship introduction, staff retention incentives, knowledge documentation, and billing system continuity.",
    keyMetrics: ["Utilization Rate", "Billing Rate", "Client Retention", "Staff Turnover", "Project Profitability"],
    valuationFactors: ["Client Contracts", "Staff Expertise", "Brand Reputation", "Recurring Revenue"],
  },

  food_beverage: {
    industry: "Food & Beverage",
    industryFr: "Alimentation et boissons",
    executiveSummaryPrompt: "Focus on brand recognition, distribution channels, product portfolio, and regulatory compliance. Highlight customer loyalty and repeat purchase rates.",
    businessOverviewPrompt: "Detail product lines, production facilities, distribution network, key accounts, and food safety certifications.",
    financialHighlightsPrompt: "Emphasize gross margins, cost of goods sold breakdown, distribution costs, and seasonality patterns.",
    growthOpportunitiesPrompt: "Identify new product development, distribution expansion, private label opportunities, and export potential.",
    investmentConsiderationsPrompt: "Address food safety compliance, ingredient sourcing, shelf life management, and competitive dynamics.",
    transitionPlanPrompt: "Focus on recipe documentation, supplier relationships, distribution agreements, and quality control procedures.",
    keyMetrics: ["Gross Margin", "Distribution Points", "Brand Awareness", "Repeat Purchase Rate", "SKU Performance"],
    valuationFactors: ["Brand Value", "Distribution Network", "Recipes/Formulas", "Customer Relationships"],
  },

  construction: {
    industry: "Construction",
    industryFr: "Construction",
    executiveSummaryPrompt: "Highlight backlog, bonding capacity, safety record, and key relationships. Emphasize project diversity and completion record.",
    businessOverviewPrompt: "Detail service capabilities, equipment fleet, key personnel, bonding capacity, and historical project portfolio.",
    financialHighlightsPrompt: "Focus on backlog value, gross margins by project type, equipment utilization, and working capital cycle.",
    growthOpportunitiesPrompt: "Identify geographic expansion, service diversification, equipment modernization, and strategic partnerships.",
    investmentConsiderationsPrompt: "Address bonding requirements, safety compliance, equipment maintenance, and project concentration risk.",
    transitionPlanPrompt: "Focus on project continuity, key employee retention, bonding transfer, and customer relationship introduction.",
    keyMetrics: ["Backlog", "Bonding Capacity", "Safety Record", "Equipment Utilization", "Project Margins"],
    valuationFactors: ["Backlog Value", "Equipment Fleet", "Bonding Capacity", "Customer Relationships"],
  },

  default: {
    industry: "General Business",
    industryFr: "Entreprise générale",
    executiveSummaryPrompt: "Provide a comprehensive overview of the business highlighting key strengths, market position, and financial performance.",
    businessOverviewPrompt: "Detail the business model, operations, customer base, competitive advantages, and growth trajectory.",
    financialHighlightsPrompt: "Present key financial metrics including revenue trends, profitability, cash flow, and capital requirements.",
    growthOpportunitiesPrompt: "Identify organic and inorganic growth opportunities, market expansion potential, and operational improvements.",
    investmentConsiderationsPrompt: "Address key risks, competitive dynamics, market trends, and due diligence considerations.",
    transitionPlanPrompt: "Outline the transition approach including owner involvement, employee retention, and operational continuity.",
    keyMetrics: ["Revenue Growth", "EBITDA Margin", "Customer Retention", "Market Share"],
    valuationFactors: ["Cash Flow", "Growth Potential", "Market Position", "Assets"],
  },
};

export const getTemplateForIndustry = (industry: string): CIMTemplate => {
  const normalizedIndustry = industry.toLowerCase().replace(/[^a-z]/g, '_');
  return CIM_TEMPLATES[normalizedIndustry] || CIM_TEMPLATES.default;
};

export const INDUSTRY_OPTIONS = Object.entries(CIM_TEMPLATES)
  .filter(([key]) => key !== 'default')
  .map(([key, template]) => ({
    value: key,
    labelEn: template.industry,
    labelFr: template.industryFr,
  }));
